// src/pages/MyBookings.jsx  (Mybookings.jsx on disk)
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import { getMyBookingsApi, submitFeedbackApi, getMyFeedbackApi } from "../api/bookingApi";
import "./my-bookings.css";

const STATUS_CONFIG = {
  pending_payment:      { label:"Pending Payment",      icon:"💳", color:"#92400E", bg:"#FEF3C7", bar:"#F59E0B", desc:"Payment not completed yet.", step:0 },
  payment_failed:       { label:"Payment Failed",       icon:"❌", color:"#991B1B", bg:"#FEE2E2", bar:"#EF4444", desc:"Your payment failed. Please try booking again.", step:0 },
  awaiting_confirmation:{ label:"Awaiting Confirmation",icon:"⏳", color:"#1E40AF", bg:"#DBEAFE", bar:"#3B82F6", desc:"Payment received! Our team will confirm your booking within 2–4 hours.", step:1 },
  confirmed:            { label:"Confirmed",            icon:"✅", color:"#065F46", bg:"#D1FAE5", bar:"#16A34A", desc:"Your booking is confirmed! Our caregiver will visit on the scheduled date.", step:2 },
  in_progress:          { label:"In Progress",          icon:"🏃", color:"#3730A3", bg:"#E0E7FF", bar:"#6366F1", desc:"Your service is currently in progress.", step:3 },
  completed:            { label:"Completed",            icon:"🎉", color:"#14532D", bg:"#D1FAE5", bar:"#16A34A", desc:"Service completed successfully. Thank you for choosing Suvidha!", step:4 },
  cancelled:            { label:"Cancelled",            icon:"🚫", color:"#6B7280", bg:"#F3F4F6", bar:"#9CA3AF", desc:"This booking has been cancelled.", step:-1 },
};

const TIME_LABELS = {
  early:"Before 08 AM", morning:"08 AM – 12 PM",
  afternoon:"12 PM – 04 PM", evening:"After 04 PM",
};

const JOURNEY_STEPS = [
  { icon:"💳", label:"Payment" },
  { icon:"⏳", label:"Awaiting" },
  { icon:"✅", label:"Confirmed" },
  { icon:"🏃", label:"In Progress" },
  { icon:"🎉", label:"Done" },
];

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" });
}

// ── Star Rating ───────────────────────────────────────────
function StarRating({ value, onChange, readonly=false }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display:"flex", gap:4 }}>
      {[1,2,3,4,5].map(s => (
        <span key={s}
          onClick={()=>!readonly&&onChange(s)}
          onMouseEnter={()=>!readonly&&setHovered(s)}
          onMouseLeave={()=>!readonly&&setHovered(0)}
          style={{ fontSize:32, cursor:readonly?"default":"pointer",
            color:s<=(hovered||value)?"#F59E0B":"#E2E8F0", userSelect:"none" }}>★</span>
      ))}
    </div>
  );
}

// ── Feedback Modal ────────────────────────────────────────
function FeedbackModal({ booking, existingFeedback, onClose, onSubmitted }) {
  const [rating,  setRating]  = useState(existingFeedback?.rating||0);
  const [comment, setComment] = useState(existingFeedback?.comment||"");
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const isView = !!existingFeedback;
  const LABELS = ["","Poor","Fair","Good","Very Good","Excellent"];

  const handleSubmit = async () => {
    if (!rating) return setError("Please select a star rating");
    setSaving(true); setError("");
    try { await submitFeedbackApi(booking.id,{rating,comment}); onSubmitted(); onClose(); }
    catch(e) { setError(e?.response?.data?.message||"Submit failed"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:2000,backdropFilter:"blur(4px)" }}/>
      <div style={{
        position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
        background:"#fff", borderRadius:22, padding:"28px 24px",
        width:"calc(100% - 32px)", maxWidth:440, zIndex:2001,
        boxShadow:"0 30px 80px rgba(0,0,0,.25)"
      }}>
        <button onClick={onClose} style={{
          position:"absolute", top:14, right:14, background:"#F1F5F9",
          border:"none", width:32, height:32, borderRadius:"50%",
          cursor:"pointer", fontSize:15, color:"#64748B"
        }}>✕</button>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ fontSize:36, marginBottom:6 }}>⭐</div>
          <h2 style={{ margin:0, fontSize:19, fontWeight:800, color:"#1E293B" }}>
            {isView?"Your Feedback":"Rate this Service"}
          </h2>
          <p style={{ margin:"4px 0 0", fontSize:13, color:"#64748B" }}>{booking.service_name}</p>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", marginBottom:16 }}>
          <StarRating value={rating} onChange={isView?()=>{}:setRating} readonly={isView} />
          {rating>0 && <div style={{ marginTop:6,fontSize:14,fontWeight:700,color:"#F59E0B" }}>{LABELS[rating]}</div>}
        </div>
        <div style={{ marginBottom:16 }}>
          <label style={{ fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:6 }}>
            {isView?"Your comment":"Write a review (optional)"}
          </label>
          <textarea value={comment} onChange={e=>!isView&&setComment(e.target.value)} readOnly={isView}
            placeholder="Share your experience..."
            style={{ width:"100%",padding:"10px 12px",borderRadius:10,border:"1px solid #CBD5E1",
              fontSize:13,resize:"vertical",minHeight:80,fontFamily:"inherit",
              boxSizing:"border-box",background:isView?"#F8FAFC":"#fff",outline:"none" }}
          />
        </div>
        {error && <div style={{ background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:8,
          padding:"9px 12px",color:"#DC2626",fontSize:13,marginBottom:12 }}>⚠️ {error}</div>}
        {!isView ? (
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={onClose} style={{ flex:1,padding:"11px",borderRadius:10,
              border:"1px solid #CBD5E1",background:"#fff",cursor:"pointer",
              fontWeight:700,fontSize:13,color:"#64748B",fontFamily:"inherit" }}>Cancel</button>
            <button onClick={handleSubmit} disabled={saving} style={{ flex:2,padding:"11px",
              borderRadius:10,border:"none",background:saving?"#93C5FD":"#1F4E8C",
              color:"#fff",cursor:saving?"not-allowed":"pointer",
              fontWeight:800,fontSize:13,fontFamily:"inherit" }}>
              {saving?"Submitting...":"⭐ Submit Feedback"}
            </button>
          </div>
        ) : (
          <button onClick={onClose} style={{ width:"100%",padding:"11px",borderRadius:10,
            border:"none",background:"#F1F5F9",cursor:"pointer",
            fontWeight:700,fontSize:13,color:"#64748B",fontFamily:"inherit" }}>Close</button>
        )}
      </div>
    </>
  );
}

// ── Booking Card ──────────────────────────────────────────
function BookingCard({ bk, onBook, myFeedback, onFeedbackSubmitted }) {
  const [expanded,     setExpanded]     = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const cfg         = STATUS_CONFIG[bk.status] || STATUS_CONFIG.cancelled;
  const isCancelled = bk.status==="cancelled"||bk.status==="payment_failed";
  const isCompleted = bk.status==="completed";
  const hasFeedback = !!myFeedback;

  return (
    <div className={`mb-card ${isCancelled?"mb-card-cancelled":""}`}>

      {/* Colour top bar */}
      <div className="mb-card-bar" style={{ background:cfg.bar }}/>

      {/* TOP ROW */}
      <div className="mb-card-top">
        <div>
          <div className="mb-bk-id">#BK{String(bk.id).padStart(5,"0")}</div>
          <div className="mb-svc-name">{bk.service_name}</div>
          <div className="mb-svc-cat">{bk.category}</div>
        </div>
        <div className="mb-card-right">
          <span className="mb-status-badge" style={{ background:cfg.bg,color:cfg.color }}>
            {cfg.icon} {cfg.label}
          </span>
          <div className="mb-amount">
            {parseFloat(bk.price)===0
              ? <span className="mb-free">FREE</span>
              : `₹${parseFloat(bk.price).toLocaleString("en-IN")}`}
          </div>
        </div>
      </div>

      {/* INFO GRID */}
      <div className="mb-info-row">
        <div className="mb-info-item">
          <span className="mb-info-icon">📅</span>
          <div>
            <div className="mb-info-label">Date</div>
            <div className="mb-info-val">{fmt(bk.booking_date)}</div>
          </div>
        </div>
        <div className="mb-info-item">
          <span className="mb-info-icon">🕐</span>
          <div>
            <div className="mb-info-label">Time Slot</div>
            <div className="mb-info-val">{TIME_LABELS[bk.time_slot]||bk.time_slot}</div>
          </div>
        </div>
        <div className="mb-info-item">
          <span className="mb-info-icon">📍</span>
          <div>
            <div className="mb-info-label">Address</div>
            <div className="mb-info-val">{bk.line1?`${bk.line1}, ${bk.city}`:"—"}</div>
          </div>
        </div>
        <div className="mb-info-item">
          <span className="mb-info-icon">💳</span>
          <div>
            <div className="mb-info-label">Payment</div>
            <div className="mb-info-val" style={{ color:bk.payment_status==="paid"?"#059669":"#92400E" }}>
              {bk.payment_status==="paid"?"✓ Paid":bk.payment_status||"—"}
            </div>
          </div>
        </div>
      </div>

      {/* STATUS MESSAGE */}
      <div className="mb-status-msg" style={{ background:cfg.bg,color:cfg.color }}>
        <span style={{ fontSize:16,flexShrink:0 }}>{cfg.icon}</span>
        <span>{cfg.desc}</span>
      </div>

      {/* JOURNEY TRACKER */}
      {!isCancelled && (
        <div className="mb-journey">
          {JOURNEY_STEPS.map((s,i) => {
            const done    = i<cfg.step;
            const current = i===cfg.step;
            return (
              <React.Fragment key={i}>
                <div className={`mb-j-step ${done?"done":""} ${current?"current":""}`}>
                  <div className="mb-j-icon">{done?"✓":s.icon}</div>
                  <div className="mb-j-label">{s.label}</div>
                </div>
                {i<JOURNEY_STEPS.length-1 && <div className={`mb-j-line ${done?"done":""}`}/>}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* EXPAND DETAILS */}
      {(bk.admin_note||bk.instructions) && (
        <button className="mb-expand-btn" onClick={()=>setExpanded(e=>!e)}>
          {expanded?"▲ Hide details":"▼ Show details"}
        </button>
      )}
      {expanded && (
        <div className="mb-expanded">
          {bk.admin_note && (
            <div className="mb-note mb-note-admin">
              <strong>📝 Admin Note:</strong><p>{bk.admin_note}</p>
            </div>
          )}
          {bk.instructions && (
            <div className="mb-note mb-note-user">
              <strong>📋 Your Instructions:</strong><p>{bk.instructions}</p>
            </div>
          )}
        </div>
      )}

      {/* FOOTER */}
      <div className="mb-card-footer">
        <div className="mb-booked-on">Booked on {fmt(bk.created_at)}</div>
        <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
          {(bk.status==="payment_failed"||bk.status==="cancelled") && (
            <button className="mb-rebook-btn" onClick={onBook}>🔄 Book Again</button>
          )}
          {isCompleted && (
            hasFeedback ? (
              <button onClick={()=>setShowFeedback(true)} style={{
                padding:"6px 12px",borderRadius:8,border:"1px solid #FDE68A",
                background:"#FFFBEB",color:"#92400E",cursor:"pointer",
                fontWeight:700,fontSize:12,fontFamily:"inherit"
              }}>⭐ {myFeedback.rating}/5 · View Feedback</button>
            ) : (
              <button onClick={()=>setShowFeedback(true)} style={{
                padding:"6px 12px",borderRadius:8,border:"none",
                background:"#1F4E8C",color:"#fff",cursor:"pointer",
                fontWeight:700,fontSize:12,fontFamily:"inherit"
              }}>⭐ Give Feedback</button>
            )
          )}
        </div>
      </div>

      {showFeedback && (
        <FeedbackModal
          booking={bk}
          existingFeedback={hasFeedback?myFeedback:null}
          onClose={()=>setShowFeedback(false)}
          onSubmitted={onFeedbackSubmitted}
        />
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────
export default function MyBookings() {
  const navigate = useNavigate();
  const [bookings,      setBookings]      = useState([]);
  const [myFeedbackMap, setMyFeedbackMap] = useState({});
  const [loading,       setLoading]       = useState(true);
  const [filter,        setFilter]        = useState("all");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [bkRes,fbRes] = await Promise.all([getMyBookingsApi(),getMyFeedbackApi()]);
      setBookings(bkRes.data.bookings||[]);
      const map={};
      (fbRes.data.feedback||[]).forEach(f=>{map[f.booking_id]=f;});
      setMyFeedbackMap(map);
    } catch(e){console.error(e);}
    finally{setLoading(false);}
  };

  useEffect(()=>{loadAll();},[]);

  const FILTERS = [
    { key:"all",                    label:"All" },
    { key:"awaiting_confirmation",  label:"⏳ Pending" },
    { key:"confirmed",              label:"✅ Confirmed" },
    { key:"in_progress",            label:"🏃 Active" },
    { key:"completed",              label:"🎉 Done" },
    { key:"cancelled",              label:"🚫 Cancelled" },
  ];

  const filtered = filter==="all" ? bookings : bookings.filter(b=>b.status===filter);
  const counts   = {
    awaiting_confirmation: bookings.filter(b=>b.status==="awaiting_confirmation").length,
    confirmed:             bookings.filter(b=>b.status==="confirmed").length,
    in_progress:           bookings.filter(b=>b.status==="in_progress").length,
    completed:             bookings.filter(b=>b.status==="completed").length,
  };

  return (
    <>
      <Header />
      <div className="mb-page">
        <div className="mb-container">

          {/* HEADER */}
          <div className="mb-header">
            <div>
              <h1 className="mb-title">My Bookings</h1>
              <p className="mb-subtitle">Track all your service bookings</p>
            </div>
            <button className="mb-new-btn" onClick={()=>navigate("/book-service")}>
              ＋ Book a Service
            </button>
          </div>

          {/* SUMMARY */}
          {!loading && bookings.length>0 && (
            <div className="mb-summary">
              <div className="mb-sum-card mb-sum-blue">
                <div className="mb-sum-num">{counts.awaiting_confirmation}</div>
                <div className="mb-sum-label">Pending</div>
              </div>
              <div className="mb-sum-card mb-sum-green">
                <div className="mb-sum-num">{counts.confirmed}</div>
                <div className="mb-sum-label">Confirmed</div>
              </div>
              <div className="mb-sum-card mb-sum-purple">
                <div className="mb-sum-num">{counts.in_progress}</div>
                <div className="mb-sum-label">Active</div>
              </div>
              <div className="mb-sum-card mb-sum-teal">
                <div className="mb-sum-num">{counts.completed}</div>
                <div className="mb-sum-label">Completed</div>
              </div>
            </div>
          )}

          {/* FILTERS */}
          {!loading && bookings.length>0 && (
            <div className="mb-filters">
              {FILTERS.map(f=>(
                <button key={f.key}
                  className={`mb-filter-btn ${filter===f.key?"active":""}`}
                  onClick={()=>setFilter(f.key)}>
                  {f.label}
                  {f.key!=="all" && (
                    <span className="mb-filter-count">
                      {bookings.filter(b=>b.status===f.key).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* CONTENT */}
          {loading ? (
            <div className="mb-loading">
              <div className="mb-spinner"/>
              <p>Loading your bookings...</p>
            </div>
          ) : bookings.length===0 ? (
            <div className="mb-empty">
              <div className="mb-empty-icon">📋</div>
              <h2>No bookings yet</h2>
              <p>Book your first service and we'll take care of the rest!</p>
              <button className="mb-new-btn" onClick={()=>navigate("/book-service")}>
                ＋ Book a Service
              </button>
            </div>
          ) : filtered.length===0 ? (
            <div className="mb-empty">
              <div className="mb-empty-icon">🔍</div>
              <h2>No bookings here</h2>
              <p>No bookings with this status yet.</p>
            </div>
          ) : (
            <div className="mb-list">
              {filtered.map(bk=>(
                <BookingCard key={bk.id} bk={bk}
                  onBook={()=>navigate("/book-service")}
                  myFeedback={myFeedbackMap[bk.id]||null}
                  onFeedbackSubmitted={loadAll}
                />
              ))}
            </div>
          )}

        </div>
      </div>
    </>
  );
}